import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Receipt, Eye, Download, PaperPlaneTilt } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { listInvoices, downloadInvoice, viewInvoice } from '../services/api';

function fmt(v, currency = 'EUR') {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  const cur = (currency || 'EUR').toUpperCase();
  const opts = { style: 'currency', currency: cur };
  if (cur === 'XPF') { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
  try { return new Intl.NumberFormat('fr-FR', opts).format(n); } catch { return `${n} ${cur}`; }
}

export default function ClientInvoicesPanel({ contactId, organizationId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = {};
    if (contactId) params.contact_id = contactId;
    if (organizationId) params.organization_id = organizationId;
    setLoading(true);
    listInvoices(params)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [contactId, organizationId]);

  const doDownload = async (inv) => {
    try {
      const r = await downloadInvoice(inv.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };
  const doView = async (inv) => {
    try {
      const r = await viewInvoice(inv.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Erreur ouverture'); }
  };

  return (
    <Card className="border-[#E8E6E1]" data-testid="client-invoices-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <Receipt size={16} className="text-[#2D5A4C]" /> Factures
            <Badge variant="outline" className="text-xs">{items.length}</Badge>
          </h2>
          <Button size="sm" variant="outline" onClick={() => navigate('/paiements/envoi')} className="border-[#E8E6E1]" data-testid="new-payment-btn">
            <PaperPlaneTilt size={14} className="mr-1" /> Nouveau paiement
          </Button>
        </div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#2D5A4C]" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#5E6D66] py-4 text-center">Aucune facture émise pour ce client.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E6E1] text-left text-xs uppercase tracking-wider text-[#5E6D66]">
                  <th className="py-2 pr-3">N°</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Référence</th>
                  <th className="py-2 pr-3">Montant</th>
                  <th className="py-2 pr-3">TVA</th>
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`client-invoice-row-${inv.id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">{inv.number}</td>
                    <td className="py-2 pr-3 text-xs text-[#5E6D66]">{inv.issued_at ? inv.issued_at.slice(0, 10) : '-'}</td>
                    <td className="py-2 pr-3 text-xs truncate max-w-[220px]">{inv.reference}</td>
                    <td className="py-2 pr-3 font-semibold">{fmt(inv.paid_amount, (inv.currency || 'eur').toUpperCase())}</td>
                    <td className="py-2 pr-3 text-xs text-[#5E6D66]">
                      {inv.tva_rate_applied != null ? `${inv.tva_rate_applied}%` : '-'}
                      {inv.territoire && <span className="ml-1 text-[10px]">({inv.territoire})</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">{inv.status || 'paid'}</Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title="Aperçu" onClick={() => doView(inv)} data-testid={`client-view-invoice-${inv.id}`}>
                          <Eye size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" title="Télécharger" onClick={() => doDownload(inv)} data-testid={`client-download-invoice-${inv.id}`}>
                          <Download size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
